import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Star, Trash2, BellRing, Plus } from "lucide-react";

function AlertForm({ schemes, onCreated }) {
  const [schemeCode, setSchemeCode] = useState("");
  const [targetNav, setTargetNav] = useState("");
  const [direction, setDirection] = useState("BELOW");

  const create = async () => {
    try {
      await clientApi.post("/alerts", { scheme_code: schemeCode, target_nav: Number(targetNav), direction });
      toast.success("Alert created");
      setTargetNav("");
      onCreated();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      <select className="w-full min-w-0 flex-1 rounded-md border border-[var(--glass-border)] bg-white p-2 text-base sm:min-w-[220px] sm:text-xs" value={schemeCode} onChange={(e) => setSchemeCode(e.target.value)} data-testid="alert-scheme-select">
        <option value="">Select a watchlisted fund...</option>
        {schemes.map((s) => <option key={s.scheme_code} value={s.scheme_code}>{s.scheme_name}</option>)}
      </select>
      <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-base sm:w-auto sm:text-xs" value={direction} onChange={(e) => setDirection(e.target.value)} data-testid="alert-direction-select">
        <option value="BELOW">NAV falls below</option>
        <option value="ABOVE">NAV rises above</option>
      </select>
      <Input className="w-full sm:w-28" type="number" placeholder="Target ₹" value={targetNav} onChange={(e) => setTargetNav(e.target.value)} data-testid="alert-target-input" />
      <Button size="sm" className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56] sm:w-auto" disabled={!schemeCode || !targetNav} onClick={create} data-testid="alert-create-button">
        <Plus className="mr-1 h-3.5 w-3.5" /> Set alert
      </Button>
    </div>
  );
}

export default function Watchlist() {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([
    clientApi.get("/watchlist").then((r) => setItems(r.data)),
    clientApi.get("/alerts").then((r) => setAlerts(r.data)),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (code) => { await clientApi.delete(`/watchlist/${code}`); load(); };
  const removeAlert = async (id) => { await clientApi.delete(`/alerts/${id}`); load(); };

  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="font-heading text-3xl text-[var(--brand)]">Watchlist & Alerts</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Track funds you're eyeing and get flagged when NAV hits your level.</p>
      </div>

      <Card className="mb-6 border-[var(--glass-border)]">
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading...</div> :
            items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[var(--ink-muted)]" data-testid="empty-watchlist">
                <Star className="mx-auto mb-2 h-8 w-8 text-[var(--cta)]" />
                Your watchlist is empty. Open any fund and tap "Watch" to add it here.
                <div className="mt-3"><Link to="/funds" className="text-[var(--cta)] hover:underline">Explore funds →</Link></div>
              </div>
            ) : (
              <div className="divide-y divide-[var(--glass-border)]">
                {items.map((it) => (
                  <div key={it.scheme_code} className="flex items-center justify-between gap-3 p-4" data-testid={`watch-row-${it.scheme_code}`}>
                    <div className="min-w-0 flex-1">
                      <Link to={`/funds/${it.scheme_code}`} className="block truncate font-medium text-[var(--brand)] hover:underline">{it.scheme_name}</Link>
                      <div className="text-xs text-[var(--ink-muted)]">{it.sub_category} · {it.riskometer}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">₹{it.nav?.toFixed(2)}</div>
                      <div className={`text-xs ${(it.day_change_pct ?? 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {it.day_change_pct != null ? `${it.day_change_pct > 0 ? "+" : ""}${it.day_change_pct}%` : "—"}
                      </div>
                    </div>
                    <button onClick={() => remove(it.scheme_code)} className="text-red-500 hover:text-red-700" data-testid={`watch-remove-${it.scheme_code}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      <Card className="glass-card border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-base text-[var(--brand)]"><BellRing className="h-4 w-4" /> NAV Alerts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <AlertForm schemes={items} onCreated={load} />
          {alerts.length > 0 && (
            <div className="divide-y divide-[var(--glass-border)] rounded-xl border border-[var(--glass-border)]">
              {alerts.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-2 p-3 text-xs" data-testid={`alert-row-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{a.scheme_name}</div>
                    <div className="text-[var(--ink-muted)]">NAV {a.direction.toLowerCase()} ₹{a.target_nav} {a.current_nav ? `· now ₹${a.current_nav.toFixed(2)}` : ""}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 font-medium ${a.status === "TRIGGERED" ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700"}`}>{a.status}</span>
                  <button onClick={() => removeAlert(a.id)} className="text-red-500 hover:text-red-700" data-testid={`alert-remove-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </ClientLayout>
  );
}
